import * as React from 'react';
import { Box, Button, Typography, Stack, IconButton, LinearProgress, Chip, Fade, Paper } from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import StreamIcon from '@mui/icons-material/Stream';
import htmlParser from '../../../../../components/HtmlParser';
import { ChatMessageProps } from './types';
import { FormattedMessage } from 'react-intl';
import SavePromptBank from '../../promptBank/SavePromptBank';

export const ChatMessage = ({ message, onManualInsert, onRemove }: ChatMessageProps) => {
	const isAssistant = !message.isUser;
	const isStreaming = message.isStreaming;
	const isToolUsage = message.isToolUsage;
	const hasStatusMessage = !!message.statusMessage;

	// Determine the appropriate styling based on message state
	const getMessageStyling = () => {
		if (isStreaming) {
			return {
				bgcolor: 'background.paper',
				border: '1px solid',
				borderColor: 'grey.300',
				borderRadius: 2,
				position: 'relative' as const,
				overflow: 'hidden' as const,
				color: 'text.primary',
			};
		}
		if (isToolUsage) {
			return {
				bgcolor: 'background.paper',
				border: '1px solid',
				borderColor: 'grey.300',
				borderRadius: 2,
				position: 'relative' as const,
				overflow: 'hidden' as const,
				color: 'text.primary',
			};
		}
		if (isAssistant) {
			return {
				float: 'left' as const,
				mr: 0,
				color: 'text.primary',
			};
		}
		return {
			float: 'right' as const,
			ml: 0,
			bgcolor: 'grey.100',
			color: 'text.primary',
		};
	};

	// Get the appropriate icon
	const getMessageIcon = () => {
		// If we have actual content (not just status), show normal AI icon even during tool usage
		const hasActualContent = message.content && message.content !== 'Käsittelen pyyntöäsi...' && message.content.length > 10;

		if (isToolUsage && !hasActualContent) {
			return <BuildIcon sx={{ fontSize: '1.5rem', color: 'blue.500' }} />;
		}
		if (isStreaming) {
			return <StreamIcon sx={{ fontSize: '1.5rem', color: 'grey.500' }} />;
		}
		if (isAssistant) {
			return <PsychologyIcon sx={{ fontSize: '1.5rem', color: 'grey.600' }} />;
		}
		return <AccountCircleIcon sx={{ fontSize: '1.5rem', color: 'grey.600' }} />;
	};

	return (
		<Fade in timeout={300}>
			<Box
				sx={{
					mb: 1,
					display: 'flex',
					justifyContent: message.isUser ? 'flex-end' : 'flex-start',
				}}
			>
				<Paper
					elevation={isStreaming || isToolUsage ? 1 : 0}
					sx={{
						position: 'relative',
						borderRadius: 2,
						m: 1,
						mt: 1,
						mb: 1,
						p: 1.5,
						maxWidth: '85%',
						...getMessageStyling(),
					}}
				>
					{/* Streaming progress indicator */}
					{isStreaming && (
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								height: 1,
								bgcolor: 'grey.400',
								opacity: 0.2,
								animation: 'pulse 3s infinite',
								'@keyframes pulse': {
									'0%': { opacity: 0.2 },
									'50%': { opacity: 0.4 },
									'100%': { opacity: 0.2 },
								},
							}}
						/>
					)}

					<Stack direction='row' spacing={1} alignItems='flex-start'>
						{getMessageIcon()}
						<Box sx={{ flex: 1 }}>
							{/* Status message display */}
							{hasStatusMessage && (
								<Box
									sx={{
										mb: 1,
										p: 1,
										borderRadius: 1,
										borderLeft: '1px solid',
										borderLeftColor: 'grey.300',
									}}
								>
									<Stack direction='row' spacing={1} alignItems='center'>
										{isToolUsage ? (
											<BuildIcon sx={{ fontSize: '0.8rem', color: 'grey.600' }} />
										) : (
											<StreamIcon sx={{ fontSize: '0.8rem', color: 'grey.600' }} />
										)}
										<Typography
											variant='caption'
											sx={{
												color: 'grey.700',
												fontWeight: 500,
												fontSize: '0.75rem',
											}}
										>
											{message.statusMessage}
										</Typography>
									</Stack>
								</Box>
							)}

							{/* Main message content */}
							<Typography
								component='div'
								variant='body1'
								sx={{
									mt: '.4rem',
									opacity: isStreaming ? 0.9 : 1,
									color: 'text.primary',
									fontWeight: isStreaming ? 500 : 400,
								}}
							>
								{htmlParser(message.content)}
							</Typography>

							{/* Streaming progress bar */}
							{isStreaming && (
								<Box sx={{ mt: 1, mb: 1 }}>
									<LinearProgress
										variant='indeterminate'
										sx={{
											height: 1,
											borderRadius: 0.5,
											'& .MuiLinearProgress-bar': {
												bgcolor: 'grey.400',
											},
										}}
									/>
								</Box>
							)}

							{/* Action buttons */}
							<Stack direction='row' justifyContent='flex-start' sx={{ mt: 1 }} spacing={1}>
								{!message.isUser && !isStreaming && (
									<Button
										size='small'
										startIcon={<ContentPasteIcon />}
										onClick={() => onManualInsert(message.content || '')}
										variant='outlined'
									>
										<FormattedMessage id='chat.insertToWord' />
									</Button>
								)}

								{message.isUser && (
									<Stack direction='row' justifyContent='flex-end'>
										<SavePromptBank content={message.content || ''} />
									</Stack>
								)}

								{!isStreaming && (
									<IconButton size='small' onClick={onRemove} color='error'>
										<DeleteIcon />
									</IconButton>
								)}
							</Stack>
						</Box>
					</Stack>
				</Paper>
			</Box>
		</Fade>
	);
};
